import React, { useState, useEffect } from 'react';
import {
  FileImage,
  Upload,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  Download,
  Send,
  AlertCircle,
  User,
  Calendar,
  Package
} from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const AdminLabels = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();

  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [uploadingMockup, setUploadingMockup] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [mockupNotes, setMockupNotes] = useState('');
  
  // Filter and search states
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Helper function to get order status badge
  const getOrderStatusBadge = (order, workflow) => {
    const orderStatus = order.status;
    const workflowStatus = workflow?.current_status;
    
    // Check for changes requested
    const hasChangesRequested = workflow?.reviews?.some(review => 
      review.status === 'revision_requested'
    );
    
    if (hasChangesRequested) {
      return <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2.5 py-0.5 rounded-full">🔄 Modifiche Richieste</span>;
    }
    
    if (orderStatus === 'label_review') {
      return <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">👁️ In Revisione</span>;
    }
    
    if (['in_production', 'to_ship', 'to_ship_gift', 'shipped', 'delivered'].includes(orderStatus)) {
      return <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">✅ Approvato</span>;
    }
    
    if (['pending_payment', 'payment_confirmed', 'waiting_label'].includes(orderStatus)) {
      return <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full">⏳ In Attesa</span>;
    }
    
    return <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">❓ {orderStatus}</span>;
  };
  const [selectedLabelFileId, setSelectedLabelFileId] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadPendingLabels();
  }, [statusFilter, searchQuery]);

  const loadPendingLabels = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      params.append('status_filter', statusFilter);
      if (searchQuery.trim()) {
        params.append('search_query', searchQuery.trim());
      }

      const response = await fetch(`${backendUrl}/api/labels/admin/pending?${params}`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPendingOrders(data || []);
      } else {
        throw new Error('Errore nel caricamento delle etichette in sospeso');
      }
    } catch (error) {
      console.error('Error loading pending labels:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare le etichette in sospeso",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast({
          title: "File troppo grande",
          description: "Il file non può superare 50MB",
          variant: "destructive"
        });
        return;
      }

      setSelectedFile(file);
    }
  };

  const uploadMockup = async () => {
    if (!selectedFile || !selectedOrder || !selectedLabelFileId) {
      toast({
        title: "Dati mancanti",
        description: "Seleziona file, ordine e etichetta cliente",
        variant: "destructive"
      });
      return;
    }

    try {
      setUploadingMockup(true);

      // DEBUG: Log what we're sending
      console.log('🔍 DEBUG MOCKUP UPLOAD:');
      console.log('📋 selectedOrder:', selectedOrder);
      console.log('🆔 selectedOrder.order.id:', selectedOrder.order.id);
      console.log('📞 selectedOrder.order.orderNumber:', selectedOrder.order.orderNumber);
      console.log('👤 selectedOrder.order.userEmail:', selectedOrder.order.userEmail);
      console.log('📁 selectedLabelFileId:', selectedLabelFileId);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('order_id', selectedOrder.order.id);
      formData.append('original_label_file_id', selectedLabelFileId);
      if (mockupNotes.trim()) {
        formData.append('notes', mockupNotes.trim());
      }

      const response = await fetch(`${backendUrl}/api/labels/admin/mockup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "Mockup caricato",
          description: "Bozza caricata e inviata al cliente",
        });

        // Reset form
        setSelectedFile(null);
        setSelectedOrder(null);
        setMockupNotes('');
        setSelectedLabelFileId('');

        // Reload data
        await loadPendingLabels();
      } else {
        throw new Error(result.message || 'Errore durante l\'upload del mockup');
      }
    } catch (error) {
      console.error('Mockup upload error:', error);
      toast({
        title: "Errore upload",
        description: error.message || 'Errore durante l\'upload del mockup',
        variant: "destructive"
      });
    } finally {
      setUploadingMockup(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'in_review':
        return <Eye className="w-5 h-5 text-blue-500" />;
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'revision_requested':
        return <MessageSquare className="w-5 h-5 text-orange-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Nuovo - Richiede revisione';
      case 'in_review':
        return 'Mockup inviato - In attesa risposta cliente';
      case 'approved':
        return 'Approvato dal cliente';
      case 'revision_requested':
        return 'Cliente richiede modifiche';
      case 'rejected':
        return 'Rifiutato dal cliente';
      default:
        return 'Stato sconosciuto';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_review':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'revision_requested':
        return 'bg-orange-100 text-orange-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCustomerInfo = (order) => {
    const shipping = order.shippingAddress || {};
    return {
      name: `${shipping.firstName || ''} ${shipping.lastName || ''}`.trim() || 'N/A',
      email: order.userEmail || 'N/A'
    };
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">🎨 Gestione Etichette Personalizzate</h2>
          <p className="text-gray-600">
            Visualizza le etichette caricate dai clienti, crea e invia bozze personalizzate.
          </p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🔍 Cerca per numero ordine
              </label>
              <input
                type="text"
                placeholder="Es: LP20251009CE4C7D"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📊 Filtra per stato
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">🗂️ Tutti gli ordini con etichette</option>
                <option value="waiting_upload">⏳ In attesa caricamento file</option>
                <option value="in_review">👁️ In revisione (da valutare)</option>
                <option value="changes_requested">🔄 Richieste modifiche</option>
                <option value="approved">✅ Approvati (in produzione)</option>
              </select>
            </div>
          </div>
        </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center">
            <Clock className="w-8 h-8 text-yellow-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">In Attesa</p>
              <p className="text-2xl font-bold text-gray-900">
                {pendingOrders.filter(order => order.workflow?.current_status === 'pending').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center">
            <Eye className="w-8 h-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">In Revisione</p>
              <p className="text-2xl font-bold text-gray-900">
                {pendingOrders.filter(order => order.workflow?.current_status === 'in_review').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center">
            <MessageSquare className="w-8 h-8 text-orange-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Richieste Modifiche</p>
              <p className="text-2xl font-bold text-gray-900">
                {pendingOrders.filter(order => order.workflow?.current_status === 'revision_requested').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Approvate</p>
              <p className="text-2xl font-bold text-gray-900">
                {pendingOrders.filter(order => order.workflow?.current_status === 'approved').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Orders List */}
      {pendingOrders.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <FileImage className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna etichetta in sospeso</h3>
          <p className="text-gray-500">
            Le etichette caricate dai clienti appariranno qui per la revisione.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingOrders.map((orderData) => {
            const order = orderData.order;
            const workflow = orderData.workflow;
            const customer = getCustomerInfo(order);
            
            return (
              <div key={order.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <Package className="w-5 h-5 text-purple-600" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          Ordine #{order.orderNumber || order.id}
                        </h3>
                        {getOrderStatusBadge(order, workflow)}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <User className="w-4 h-4" />
                          <span>{customer.name}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(order.createdAt).toLocaleDateString('it-IT')}</span>
                        </div>
                        <span>€{order.totalAmount?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(workflow?.current_status || 'pending')}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Customer Files */}
                    <div>
                      <h4 className="text-md font-semibold text-gray-900 mb-3">File Cliente</h4>
                      {(() => {
                        // Check for files in workflow.customer_files (normal upload flow)
                        const workflowFiles = workflow?.customer_files || [];
                        
                        // Check for files in order items (Builder upload flow)
                        const builderFiles = [];
                        if (orderData?.order?.items) {
                          orderData.order.items.forEach((item, index) => {
                            // Add label image if present
                            if (item.labelImage || item.label?.image) {
                              const imagePath = item.labelImage || item.label?.image;
                              builderFiles.push({
                                id: `builder_label_${index}_${imagePath}`,
                                file_path: imagePath,
                                original_filename: `Etichetta ${item.name || 'Personalizzata'}.png`,
                                file_size: 0,
                                uploaded_at: orderData.order.createdAt,
                                notes: `Etichetta originale - ${item.name || 'prodotto personalizzato'}`,
                                isBuilderFile: true,
                                fileType: 'label'
                              });
                            }
                            
                            // Add bottle preview if present
                            if (item.bottlePreview || item.label?.bottlePreview) {
                              const previewPath = item.bottlePreview || item.label?.bottlePreview;
                              builderFiles.push({
                                id: `builder_preview_${index}_${previewPath}`,
                                file_path: previewPath,
                                original_filename: `Anteprima 3D ${item.name || 'Bottiglia'}.png`,
                                file_size: 0,
                                uploaded_at: orderData.order.createdAt,
                                notes: `Anteprima 3D generata con Nano Banana - ${item.name || 'prodotto personalizzato'}`,
                                isBuilderFile: true,
                                fileType: 'preview'
                              });
                            }
                          });
                        }
                        
                        const allFiles = [...workflowFiles, ...builderFiles];
                        
                        return allFiles.length > 0 ? (
                        <div className="space-y-3">
                          {allFiles.map((file) => (
                            <div key={file.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                              <div className="flex items-center space-x-3">
                                {/* Thumbnail preview */}
                                <div className="w-16 h-16 flex-shrink-0 bg-white rounded border border-gray-200 overflow-hidden">
                                  <img 
                                    src={file.file_path.startsWith('http') ? file.file_path : `${backendUrl}/api/upload/serve/${file.file_path}`}
                                    alt={file.original_filename}
                                    className="w-full h-full object-contain"
                                    onError={(e) => {
                                      console.error('❌ Image load error:', file.file_path);
                                      e.target.style.display = 'none';
                                      const fallback = e.target.nextElementSibling;
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                  />
                                  <div style={{display: 'none'}} className="w-full h-full flex items-center justify-center">
                                    <FileImage className="w-8 h-8 text-gray-400" />
                                  </div>
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{file.original_filename}</p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(file.file_size)} • {new Date(file.uploaded_at).toLocaleDateString('it-IT')}
                                  </p>
                                  {file.notes && (
                                    <p className="text-xs text-gray-600 mt-1">Note: {file.notes}</p>
                                  )}
                                  {file.fileType && (
                                    <p className="text-xs text-purple-600 mt-1">
                                      {file.fileType === 'preview' ? '🎨 Anteprima 3D' : '📄 Etichetta'}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => {
                                    // Handle both full URLs and relative paths
                                    let fullUrl;
                                    if (file.file_path.startsWith('http')) {
                                      // Already a full URL
                                      fullUrl = file.file_path;
                                    } else {
                                      // Relative path, construct full URL
                                      fullUrl = `${backendUrl}/api/upload/serve/${file.file_path}`;
                                    }
                                    console.log('🔍 Opening file:', fullUrl);
                                    console.log('📁 File path:', file.file_path);
                                    console.log('🏷️ File type:', file.fileType);
                                    window.open(fullUrl, '_blank');
                                  }}
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  Visualizza
                                </button>
                                {!selectedOrder && (
                                  <button
                                    onClick={() => {
                                      console.log('🔍 DEBUG ORDER SELECTION:');
                                      console.log('📋 orderData:', orderData);
                                      console.log('🆔 orderData.order.id:', orderData.order.id);
                                      console.log('📞 orderData.order.orderNumber:', orderData.order.orderNumber);
                                      console.log('📁 file.id:', file.id);
                                      console.log('🏗️ file.isBuilderFile:', file.isBuilderFile);
                                      setSelectedOrder(orderData);
                                      setSelectedLabelFileId(file.isBuilderFile ? file.file_path : file.id);
                                    }}
                                    className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                                  >
                                    Crea Bozza
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        ) : (
                          <p className="text-gray-500 text-sm">Nessun file caricato dal cliente</p>
                        );
                      })()}
                    </div>

                    {/* Admin Mockups */}
                    <div>
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Bozze Inviate</h4>
                      {workflow?.admin_mockups && workflow.admin_mockups.length > 0 ? (
                        <div className="space-y-3">
                          {workflow.admin_mockups.map((mockup) => (
                            <div key={mockup.id} className="flex items-center justify-between bg-amber-50 p-3 rounded-lg border border-amber-200">
                              <div className="flex items-center space-x-3">
                                <Download className="w-5 h-5 text-amber-600" />
                                <div>
                                  <p className="text-sm font-medium">{mockup.original_filename}</p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(mockup.file_size)} • {new Date(mockup.uploaded_at).toLocaleDateString('it-IT')}
                                  </p>
                                  {mockup.notes && (
                                    <p className="text-xs text-gray-600 mt-1">Note: {mockup.notes}</p>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => window.open(`${backendUrl}/api/upload/serve/${mockup.file_path}`, '_blank')}
                                className="text-amber-600 hover:text-amber-800 text-sm font-medium"
                              >
                                Visualizza
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">Nessuna bozza inviata</p>
                      )}
                    </div>
                  </div>

                  {/* Reviews */}
                  {workflow?.reviews && workflow.reviews.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Cronologia Revisioni</h4>
                      <div className="space-y-2">
                        {workflow.reviews.map((review) => (
                          <div key={review.id} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(review.status)}`}>
                                {getStatusText(review.status)}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(review.updated_at).toLocaleDateString('it-IT')}
                              </span>
                            </div>
                            {review.customer_comments && (
                              <p className="text-sm text-gray-700">
                                <strong>Commenti cliente:</strong> {review.customer_comments}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Mockup Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Carica Bozza Etichetta - Ordine #{selectedOrder.order.orderNumber || selectedOrder.order.id}
            </h3>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Cliente:</strong> {getCustomerInfo(selectedOrder.order).name} ({getCustomerInfo(selectedOrder.order).email})
              </p>
            </div>

            {/* File Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Carica Bozza Etichetta
              </label>
              <input
                type="file"
                onChange={handleFileSelect}
                className="w-full p-2 border border-gray-300 rounded-lg"
                accept="image/*,.pdf"
              />
              {selectedFile && (
                <p className="text-xs text-gray-500 mt-1">
                  File selezionato: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              )}
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note per il cliente (opzionale)
              </label>
              <textarea
                placeholder="Inserisci eventuali note o spiegazioni per il cliente..."
                value={mockupNotes}
                onChange={(e) => setMockupNotes(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg"
                rows="3"
              />
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={uploadMockup}
                disabled={!selectedFile || uploadingMockup}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {uploadingMockup ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Caricamento...
                  </div>
                ) : (
                  <>
                    <Send className="w-4 h-4 inline-block mr-2" />
                    Invia Bozza al Cliente
                  </>
                )}
              </button>
              
              <button
                onClick={() => {
                  setSelectedOrder(null);
                  setSelectedFile(null);
                  setMockupNotes('');
                  setSelectedLabelFileId('');
                }}
                className="bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default AdminLabels;